import { useMemo, useState } from 'react'
import { Box, Divider, Typography, Tooltip, IconButton } from '@mui/material'
import type { ColumnDef } from '@tanstack/react-table'
import { TanStackTable } from '@/@core/components/mui/TanStackTable'
import { TablePagination, TablePaginationProps } from '@/@core/components/mui/TablePagination'
import {
  CompanyStatusEnum,
  ICompany,
  PaymentPartnerEnum,
  useCompanyStatusMutation,
  useCompanyChangeStatusMutation,
  useExportEmployeerMutation
} from '@/@data'
import dayjs from 'dayjs'
import { Button } from '@/@core/components/mui/Button'
import { useRowSelection } from '@/@core/hooks'
import { EmployerStatusDropDown } from './EmployerStatusDropDown'
import { CompanyLogo } from '../common/CompanyLogo'
import { useNotification, useRouteQuery } from '@/hooks'
import { useTranslator } from '@/hooks/useTranslator'
import { useNavigate } from 'react-router-dom'

interface EmployerTableProps {
  data: ICompany[]
  meta: unknown
  onRowClick?: (employer: ICompany) => void
  onEdit?: (employer: ICompany) => void
  refetch: () => void
}

export const EmployerTable = ({ data, meta, onEdit, refetch }: EmployerTableProps) => {
  const { __ } = useTranslator()
  const { rowSelection, setRowSelection, selectedRows } = useRowSelection(data)
  const { mutate: updateCompanyStatus } = useCompanyStatusMutation()
  const changeStatusCompanyMutation = useCompanyChangeStatusMutation()
  const n = useNotification()
  const exportEmployerMutation = useExportEmployeerMutation()
  const [selectedStatus, setSelectedStatus] = useState<CompanyStatusEnum>(CompanyStatusEnum.Active)
  const { searchQuery } = useRouteQuery()
  const navigate = useNavigate()

  const handleBulkStatusChange = () => {
    const selectedIds = selectedRows.map((c) => c.id)

    if (selectedIds.length === 0) {
      n.error(__('Please select at least one company'))

      return
    }

    changeStatusCompanyMutation.mutate(
      {
        data: {
          status: selectedStatus,
          companyIds: selectedIds as number[]
        }
      },
      {
        onSuccess: () => {
          setRowSelection({})
          refetch()
          n.success(__('Company status updated successfully'))
        },
        onError: error => {
          n.error(error.message)
        }
      }
    )
  }

  const columns = useMemo<ColumnDef<ICompany>[]>(
    () => [
      {
        accessorKey: 'employer',
        header: __('Employer'),
        size: 250,
        cell: ({ row }) => <CompanyLogo company={row.original} />
      },
      {
        accessorKey: 'employee',
        header: __('Employee'),
        cell: ({ row }) => (
          <div className='flex items-center gap-2'>
            <i className='tabler-users text-[#3551F2]' />
            <Typography className='text-[15px] leading-[22px] font-normal'>{row.original.total_user}</Typography>
          </div>
        ),
        size: 100
      },
      {
        accessorKey: 'email',
        header: __('Email'),
        cell: ({ row }) => (
          <Typography variant='body2' color='text.primary'>
            {row.original.email}
          </Typography>
        ),
        size: 200,
      },
      {
        accessorKey: 'start_date',
        header: __('Start Date'),
        cell: ({ row }) => {
          const startDate = row.original.active_contract?.start_date;

          return (
            <Typography variant='body2' color='text.primary'>
              {startDate ? dayjs(startDate).format('DD/MM/YYYY') : '-'}
            </Typography>
          )
        }
      },
      {
        accessorKey: 'end_date',
        header: __('End Date'),
        cell: ({ row }) => {
          const endDate = row.original.active_contract?.end_date
          const isExpired = endDate ? dayjs(endDate).isBefore(dayjs(), 'day') : false

          return (
            <Box className='flex items-center gap-2'>
              <Typography variant='body2' color='text.primary'>
                {endDate ? dayjs(endDate).format('DD/MM/YYYY') : '-'}
              </Typography>

              {isExpired && (
                <Tooltip title={__('Expired')} placement='right' arrow>
                  <i className='tabler-info-circle' style={{ color: '#EA5455' }} />
                </Tooltip>
              )}
            </Box>
          )
        }
      },
      {
        accessorKey: 'payment_partner',
        header: __('Payment Partner'),
        cell: ({ row }) => (
          <Typography variant='body2' color='text.primary'>
            {row.original.payment_partner === PaymentPartnerEnum.Shinhan ? 'Shinhan Bank' : 'VNPT ePay'}
          </Typography>
        ),
        size: 140
      },
      {
        accessorKey: 'status',
        header: __('Status'),
        cell: ({ row }) => {
          const employer = row.original

          return (
            <EmployerStatusDropDown
              status={employer.status}
              sx={{
                height: '30px'
              }}
              onSelect={status => {
                updateCompanyStatus(
                  {
                    id: employer.id,
                    data: {
                      status
                    }
                  },
                  {
                    onSuccess: () => {
                      refetch()
                      n.success(__('Employer status updated successfully'))
                    },
                    onError: error => {
                      n.error(error.message)
                    }
                  }
                )
              }}
            />
          )
        }
      },
      {
        accessorKey: 'actions',
        header: __('Actions'),
        cell: ({ row }) => {
          return (
            <IconButton size='small' className='flex items-center gap-4' onClick={() => navigate(`/cms/employer/${row.original.id}`)}>
              <i className='tabler-edit text-[#2F2B3DB2] size-[20px]' />
            </IconButton>
          )
        },
        size: 80
      }
    ],
    [onEdit]
  )

  return (
    <>
      {Object.keys(rowSelection).length > 0 && (
        <>
          <Divider />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px'
            }}
          >
            <Box>
              <Button variant='outlined' color='secondary' className='mr-2'>
                {Object.keys(rowSelection).length} {__('Selected')}
              </Button>
              <EmployerStatusDropDown
                status={selectedStatus}
                onSelect={(status) => setSelectedStatus(status)}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 3 }}>
              <Button
                variant='tonal'
                color='primary'
                onClick={() => {
                  const selectedComanyIds = Object.keys(rowSelection).map(Number)

                  exportEmployerMutation.mutate(selectedComanyIds, {
                    onError: () => {
                      n.error(__('Failed to export employee data'))
                    }
                  })
                }}
                disabled={exportEmployerMutation.isPending}
                startIcon={<i className='tabler-upload' />}
              >
                {exportEmployerMutation.isPending ? __('Exporting...') : __('Export Employer')}
              </Button>

              <Button
                variant='contained'
                size='small'
                startIcon={<i className='tabler-check' />}
                sx={{ height: 40 }}
                onClick={handleBulkStatusChange}
                disabled={
                  Object.keys(rowSelection).length === 0
                }
                loading={changeStatusCompanyMutation.isPending}
              >
                {__('Submit')}
              </Button>

              <Box>
                <Button
                  variant='tonal'
                  color='secondary'
                  onClick={() => setRowSelection({})}
                  startIcon={<i className='tabler-x' />}
                >
                  {__('Clear')}
                </Button>
              </Box>
            </Box>
          </Box>
        </>
      )}

      <TanStackTable
        columns={columns}
        tableData={data}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        enableRowSelection
        borderRight='2px solid #e0e0e0'
      />

      {meta && (
        <TablePagination
          meta={
            {
              ...meta,
              page: Number(searchQuery.get('page')) || 1
            } as unknown as TablePaginationProps
          }
        />
      )}
    </>
  )
}
